import React, { useReducer, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import { ButtonLink } from 'components/Button';
import {
  resendReducer,
  initialResendState,
  RESEND_ACTIONS,
  RESEND_COOLDOWN_SECONDS,
  RESEND_ATTEMPT_CAP,
} from './resendReducer';

/**
 * useResend — wires the pure resendReducer (Requirement 11) into React state and
 * drives the 1Hz countdown tick. The screen owns the actual Cognito resend call;
 * this hook only owns the cooldown / attempt-cap state machine.
 *
 * @param {(helpers: { onFailure: () => void }) => (void | Promise<any>)} onResend
 *   Callback fired when a resend is actually issued (i.e. the control was enabled).
 *   It receives an `onFailure` helper the caller invokes (or the returned promise
 *   rejects) to reset the cooldown per Req 11.4.
 * @returns {{
 *   remainingSeconds: number,
 *   attemptCount: number,
 *   disabled: boolean,
 *   attemptsExhausted: boolean,
 *   requestResend: () => void,
 *   resendFailed: () => void,
 * }}
 */
export const useResend = (onResend) => {
  const [state, dispatch] = useReducer(resendReducer, initialResendState);
  const { remainingSeconds, attemptCount, disabled } = state;

  // 1Hz tick: only runs while a cooldown is active, cleaned up on unmount/change.
  useEffect(() => {
    if (remainingSeconds <= 0) return undefined;
    const intervalId = setInterval(() => {
      dispatch({ type: RESEND_ACTIONS.TICK });
    }, 1000);
    return () => clearInterval(intervalId);
  }, [remainingSeconds]);

  const resendFailed = useCallback(() => {
    dispatch({ type: RESEND_ACTIONS.RESEND_FAILED });
  }, []);

  const requestResend = useCallback(() => {
    // Reject requests while disabled (mirrors the reducer's own guard, but avoids
    // firing the caller's side-effect when cooldown/cap is active — Req 11.2, 11.5).
    if (disabled) return;
    dispatch({ type: RESEND_ACTIONS.REQUEST_RESEND });

    if (typeof onResend !== 'function') return;
    const result = onResend({ onFailure: resendFailed });
    // Support both promise-returning and callback-driven resend handlers.
    if (result && typeof result.then === 'function') {
      result.catch(() => resendFailed());
    }
  }, [disabled, onResend, resendFailed]);

  return {
    remainingSeconds,
    attemptCount,
    disabled,
    attemptsExhausted: attemptCount >= RESEND_ATTEMPT_CAP,
    requestResend,
    resendFailed,
  };
};

/**
 * ResendControl — presentational resend link that renders the disabled state and
 * live countdown driven by useResend. Screens can either use this component
 * directly or consume the useResend hook for custom markup.
 */
const ResendControl = ({
  onResend,
  className,
  idleLabel,
  exhaustedLabel,
  renderCountdown,
}) => {
  const {
    remainingSeconds,
    disabled,
    attemptsExhausted,
    requestResend,
  } = useResend(onResend);

  let label;
  if (attemptsExhausted) {
    label = exhaustedLabel;
  } else if (remainingSeconds > 0) {
    label = renderCountdown(remainingSeconds);
  } else {
    label = idleLabel;
  }

  return (
    <ButtonLink
      className={className}
      isDisabled={disabled}
      onClick={requestResend}
    >
      {label}
    </ButtonLink>
  );
};

ResendControl.propTypes = {
  /** Fired when a resend is actually issued. Receives { onFailure } and may return a promise. */
  onResend: PropTypes.func,
  className: PropTypes.string,
  /** Label shown when the control is enabled and idle. */
  idleLabel: PropTypes.node,
  /** Label shown once the 5-resend cap is reached (Req 11.5). */
  exhaustedLabel: PropTypes.node,
  /** Renders the countdown label from the remaining seconds. */
  renderCountdown: PropTypes.func,
};

ResendControl.defaultProps = {
  onResend: () => {},
  className: '',
  idleLabel: "Didn't receive the code? Resend",
  exhaustedLabel: 'Maximum resend attempts reached',
  renderCountdown: (seconds) => `Resend available in ${seconds}s`,
};

export default ResendControl;

export { RESEND_COOLDOWN_SECONDS, RESEND_ATTEMPT_CAP };
